import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Box, Grid, Stack, Tooltip, Typography, useTheme, Chip, IconButton, Dialog, DialogContent, DialogTitle, Button, lighten } from '@mui/material';
import { sentenceCase } from 'change-case';
import { ReactComponent as CloseIcon } from '../../../assets/icons/main/close-icon.svg';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../utils/table-utils';
import { get } from 'lodash';
import { palette } from '../../../theme/palette';
import MasterComponent from '../../../common/MasterComponent';
import { maintenanceManagementActions } from '../../../store/reducers/maintenance-management';
import { ActivityHistoryPayloadTypes } from '../../../store/models/machinery-management';
import { pageActions } from '../../../store/reducers/page-actions';

const Styles = {
  dialogTitle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1.3rem',
    padding: '0px 0px 21px 0px',
    fontFamily: 'Open Sans',
    fontWeight: '400',
  },
  imageModal: {
    paperProps: {
      sx: {
        padding: '32px',
        borderRadius: '18px',
        width: '45%',
        maxWidth: '45%',
        height: '90%',
      },
    },
    dailogContent: {
      '&.MuiDialogContent-root': {
        padding: '16px',
        borderRadius: '18px',
        border: 'none',
        boxShadow: '0px 0px 14px 0px #0000000D'
      },
    },
    headerWrapper: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2,
    },
    bodyWrapper: {
      paddingBottom: '16px',
      height: 'calc(100vh - 292px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: '1px solid transparent',
      borderRadius: '8px',
    },
    closeBtnWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      pt: 3,
      pb: 1,
    },
  },
};

interface ActivityHistoryTypes {
  isActivityHistoryLoading: false,
  isActivityHistoryHaveError: false,
  activityHistoryData: Array<any>;
  activityHistoryTotalCount: 0,
  activityHistoryPageNumber: 0,
  activityHistorySearchItem: '',
  maintenanceName: '',
}

const ImageModalWrapper = ({ open, handleClose, image, photoNum }: any) => {
  const theme = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  return (
    <Dialog
      open
      fullWidth
      PaperProps={{
        sx: {
          ...Styles.imageModal.paperProps.sx,
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <DialogTitle sx={Styles.dialogTitle} >
        Image {photoNum}
        <IconButton onClick={() => {
          handleClose();
        }}>
          <Box sx={Styles.imageModal.headerWrapper}>
            <CloseIcon />
          </Box>
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          ...Styles.imageModal.dailogContent,
          border: `1px solid  ${theme.palette.customBorderColor.main}`,
          backgroundColor: themeMode.isLightBG
            ? lighten(theme.palette.background.default, 0.9)
            : lighten(theme.palette.background.default, 0.05),
        }}
      >
        <Box sx={Styles.imageModal.bodyWrapper}>
          <Box sx={{
            width: '100%',
            height: '100%',
            backgroundImage: `url(${image})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
          }}>

          </Box>
        </Box>
        <Box sx={Styles.imageModal.closeBtnWrapper}>
          <Button variant='outlined' onClick={handleClose}>
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const ActivityHistory = ({
  isActivityHistoryLoading,
  activityHistoryData,
  activityHistoryTotalCount,
  activityHistoryPageNumber,
  maintenanceName,
}: ActivityHistoryTypes) => {
  const theme : any  = useTheme();
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchValue, setSearchValue] = useState<any>('')
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const params = useParams();
  const mainId = get(params, 'maintId', '');
  const taskId = get(params, 'actiId', '')
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname.length) {
      const pathnamesTemp: any = pathname.split('/').filter(Boolean);
      if (pathnamesTemp.length) {
        const pathnamesData: any = pathnamesTemp
          .filter((i: any, index: number) => index < 3)
          .map((item: any, index: number) => {
            if (index == 0) {
              return { route: pathnamesTemp[0], pathName: 'Maintenance Management', navigate: true };
            } else if (index == 1) {
              return { route: pathnamesTemp[0] + '/' + pathnamesTemp[1] + '/' + mainId, pathName: maintenanceName, navigate: true };
            } else {
              return { route: '', pathName: 'View Activity Details', navigate: false };
            }
          });
        dispatch(pageActions.setPagePath(pathnamesData));
        return () => {
          const data: any = [];
          dispatch(pageActions.setPagePath(data));
        };
      }
    }
  }, [pathname, maintenanceName, mainId]);

  const dipatchActivityHistoryListRequest = (payload: ActivityHistoryPayloadTypes) => {
    dispatch(
      maintenanceManagementActions.getActivityHistoryRequest({
        ...sortingModel,
        search_item: searchValue,
        ...payload,
        maintId: mainId,
        taskId: taskId
      })
    );
  };
  useEffect(() => {
    if (taskId && mainId) {

      dipatchActivityHistoryListRequest({
        limit: DEFAULT_PAGE_SIZE,
        page: DEFAULT_PAGE_NUMBER,
      });
    }
  }, [taskId, mainId]);


  const handleOpen = (photo: any, index: number) => {
    setSelectedImage(photo);
    setSelectedImageIndex(index + 1)
    setOpen(true);
  }

  const handleClose = () => {
    setSelectedImage(null);
    setOpen(false);
  }

  const columns: any = [
    {
      field: 'task_activity_name',
      headerName: 'Activity name',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 200,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.value} placement='bottom' arrow>
            <Typography
              component='p'
              variant='subtitle1'
              sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'task_activity_photos_urls',
      headerName: 'Photos(s)',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 400,
      sortable: true,
      renderCell: (params: any) => {
        let iotPhotos = [];
        if (params.value) {
          iotPhotos = params.value;
        } else {
          iotPhotos = []
        }

        return (
          <>
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', margin: '2px 0', flexWrap: 'wrap', padding: '16px 0' }}>
              {(iotPhotos.length > 0) ? iotPhotos.map((photo: any, index: number) => (
                <Box
                  key={index} onClick={() => handleOpen(photo, index)} display='flex' flexDirection='column' p={2} border={1} borderColor={theme.palette.customBorderColor.main} borderRadius={2} sx={{ width: '50px', height: '50px', cursor: 'pointer' }}>
                  <img src={photo} alt={`Image ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              )) : <Box></Box>}
            </Box>
          </>
        );
      },
    },
    {
      field: 'new_parts_info',
      headerName: 'Part(s) replace & quantity',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 400,
      sortable: true,
      renderCell: (params: any) => {
        const partsAndQuantity = params.value;
        return (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Box mt={1}>
                  {(partsAndQuantity || []).map((parts: any) => (
                    <Typography variant='subtitle1'>
                      {parts.part_name} ({parts.count})
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </>
        );
      },
    },
    {
      field: 'activity_remarks',
      headerName: 'Remark',
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 300,
      sortable: true,
    },
    {
      field: 'activity_status',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'center',
      minWidth: 200,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <>
            <Chip
              label={sentenceCase(params.value ?? '')}
              className={params.value === 'completed' ? 'Completed' : params.value}
              variant='outlined'
              style={{margin:'18.39px 0px'}}
              sx={
                  params.value === 'completed' ?
                  { backgroundColor: palette.chipSuccess.light, color: palette.chipSuccess.dark, borderColor: palette.chipSuccess.dark }
                  : { backgroundColor: palette.chipWarning.light, color: palette.chipWarning.dark, borderColor: palette.chipWarning.dark }
              }
            />
          </>
        );
      },
    },
  ];

  return (
    <>
      <MasterComponent
        heading={`Maintenance ${maintenanceName} Activity History`}
        rows={activityHistoryData}
        isLoading={isActivityHistoryLoading}
        isServerPagination
        columns={columns}
        totalRowCount={activityHistoryTotalCount}
        pageNumber={activityHistoryPageNumber}
        onPagination={(pageSize: number, pageNumber: number) => {
          dipatchActivityHistoryListRequest({
            limit: pageSize,
            page: pageNumber,
          });
        }}
        onSorting={(sortModel: { field: string; sort: string }) => {
          dipatchActivityHistoryListRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: activityHistoryPageNumber,
            ...sortModel,
          });
          setSortingModel(sortModel);
        }}
        noRowsOverlay={() => (
          <Stack height='100%' alignItems='center' justifyContent='center'>
            Activity History Not Available
          </Stack>
        )}
        hideAddNewButton
        isTemplate={true}
        ddataColumns={['activity_name', 'photos', 'parts_qualtiy', 'remark', 'status',]}
        showSearch
        searchPlaceholder='Search'
        onSearch={(value: string) => {
          dipatchActivityHistoryListRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            search_item: value,
          });
          setSearchValue(value);
        }}
        otherProps={{
          getRowHeight: () => 'auto',
          getRowId: (row: any) => row.task_activity_id,
        }}
      />
      {selectedImage && (
        <ImageModalWrapper open={open} handleClose={handleClose} image={selectedImage} photoNum={selectedImageIndex} />
      )}

    </>
  );
};

const mapStateToProps = (state: any) => ({
  isActivityHistoryLoading: state.maintenanceManagement.isActivityHistoryLoading,
  isActivityHistoryHaveError: state.maintenanceManagement.isActivityHistoryHaveError,
  activityHistoryData: state.maintenanceManagement.activityHistoryData,
  activityHistoryTotalCount: state.maintenanceManagement.activityHistoryTotalCount,
  activityHistoryPageNumber: state.maintenanceManagement.activityHistoryPageNumber,
  activityHistorySearchItem: state.maintenanceManagement.activityHistorySearchItem,
  maintenanceName: state.maintenanceManagement.maintenanceName,
});

export default connect(mapStateToProps)(ActivityHistory);
